import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EdHubDetailShell } from "../components/education/EdHubDetailShell";
import { useAnalytics } from "../lib/analytics";
import { formatToSegment } from "../lib/edhub/formatToSegment";
import { type DerivedVideoMeta, deriveVideoMeta } from "../lib/edhub/videoMeta";
import { useClientTranslations } from "../lib/i18n/client";
import {
	type Article,
	type CardDeck,
	type EducationHubArticleDetail,
	fetchArticle,
	fetchArticleDetail,
	fetchCardDeck,
	resolveMediaUrl,
	saveEducationProgress,
} from "../services/educationService";
import styles from "./EducationVideo.module.css";

/**
 * Video player. Sibling of {@link EducationPodcast}, same detail shell.
 *
 * Streams from the presigned URL `detail.videoUrl` (S3 mp4 written by the
 * prism video worker) through a NATIVE <video> element — no player library, so
 * captions/PiP/fullscreen come from the platform and RTL is handled by the
 * browser chrome. Until the worker lands, videoUrl is null and we show a
 * pending state.
 *
 * era-api exposes no video meta sidecar, so the description ("About") and the
 * "In this video" bullets are DERIVED client-side from the article intro and
 * the card questions. See `lib/edhub/videoMeta`.
 */
// Persist playback progress at most this often while playing; pause / ended /
// unmount always flush immediately so we never lose the last position.
const PROGRESS_SAVE_INTERVAL_MS = 15_000;

export function EducationVideo() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	// Resume position handed in by the Resume carousel via router state.
	const resumeAt = (location.state as { resumeAt?: number } | null)?.resumeAt;
	const { logEducation } = useAnalytics();
	const { t, locale } = useClientTranslations("education");
	const [detail, setDetail] = useState<EducationHubArticleDetail | null>(null);
	const [artefacts, setArtefacts] = useState<{
		article: Article | null;
		deck: CardDeck | null;
	}>({ article: null, deck: null });
	const meta: DerivedVideoMeta = useMemo(
		() => deriveVideoMeta(artefacts.article, artefacts.deck),
		[artefacts],
	);
	const [videoError, setVideoError] = useState(false);
	const [videoSrc, setVideoSrc] = useState<string | null>(null);
	const [posterSrc, setPosterSrc] = useState<string | undefined>(undefined);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	// Latest playback position, kept in a ref so the unmount cleanup can flush
	// it without re-subscribing the effect on every tick.
	const progressRef = useRef({ current: 0, duration: 0 });
	const lastSaveRef = useRef(0);

	// Upsert the current video position to era-api. Returns the in-flight request
	// so callers that navigate away (close) can await it — otherwise the
	// fire-and-forget PUT races the Hub's GET /progress and the resume card shows
	// a stale position. A failed save never disrupts the UI, and we no-op until
	// the duration is known.
	const saveProgress = (): Promise<void> => {
		// Prefer the live element so a direct seek that hasn't emitted a timeupdate
		// yet is still persisted; fall back to the ref when the element is gone.
		const el = videoRef.current;
		const duration =
			el && Number.isFinite(el.duration) && el.duration > 0
				? el.duration
				: progressRef.current.duration;
		if (!id || duration <= 0) return Promise.resolve();
		const raw = el ? el.currentTime : progressRef.current.current;
		const current = Math.min(Math.max(0, raw), duration);
		const progress = Math.min(1, Math.max(0, current / duration));
		return saveEducationProgress(id, {
			progress,
			currentTime: current,
			contentType: "video",
		}).catch(() => {});
	};

	// Flush the position when leaving the player OR when the content id changes
	// without unmounting (React Router reuses this component across
	// `/education/:id/video` params). Keying on `id` makes the cleanup capture
	// the correct id, so we never PUT under a stale content id.
	// biome-ignore lint/correctness/useExhaustiveDependencies: flush uses current id; saveProgress is a stable-enough closure
	useEffect(() => {
		return () => {
			void saveProgress();
		};
	}, [id]);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (hasTrackedPageView.current) return;
		hasTrackedPageView.current = true;
		logEducation("video_page_viewed", { contentId: id });
	}, [logEducation, id]);

	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		fetchArticleDetail(id)
			.then(async (d) => {
				if (cancelled) return;
				setDetail(d);
				const [article, deck] = await Promise.all([
					d.articleUrl
						? fetchArticle(d.articleUrl).catch(() => null)
						: Promise.resolve<Article | null>(null),
					d.cardsUrl
						? fetchCardDeck(d.cardsUrl).catch(() => null)
						: Promise.resolve<CardDeck | null>(null),
				]);
				if (!cancelled) setArtefacts({ article, deck });
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [id]);

	// Proxy-mode video URLs are auth-gated relative paths a native <video> can't
	// load directly; resolveMediaUrl fetches them with the bearer token and
	// returns a blob object URL (revoked on cleanup). Presigned S3 URLs pass
	// through unchanged.
	useEffect(() => {
		const url = detail?.videoUrl;
		if (!url) return;
		let revoked: string | null = null;
		let cancelled = false;
		setVideoError(false);
		resolveMediaUrl(url)
			.then((resolved) => {
				if (cancelled) {
					if (resolved.startsWith("blob:")) URL.revokeObjectURL(resolved);
					return;
				}
				if (resolved.startsWith("blob:")) revoked = resolved;
				setVideoSrc(resolved);
			})
			.catch(() => {
				if (!cancelled) setVideoError(true);
			});
		return () => {
			cancelled = true;
			if (revoked) URL.revokeObjectURL(revoked);
		};
	}, [detail?.videoUrl]);

	// The poster is the same auth-gated artefact as the cover image: resolve it
	// through the media helper so a proxy-mode path becomes a blob the native
	// element can paint. A failed poster is not an error — the player just
	// starts on its own first frame.
	useEffect(() => {
		const url = detail?.imageUrl;
		if (!url) return;
		let revoked: string | null = null;
		let cancelled = false;
		resolveMediaUrl(url)
			.then((resolved) => {
				if (cancelled) {
					if (resolved.startsWith("blob:")) URL.revokeObjectURL(resolved);
					return;
				}
				if (resolved.startsWith("blob:")) revoked = resolved;
				setPosterSrc(resolved);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
			if (revoked) URL.revokeObjectURL(revoked);
		};
	}, [detail?.imageUrl]);

	return (
		<EdHubDetailShell
			availableFormats={["text", "cards", "audio", "video"]}
			onFormatSelect={(format) =>
				id && navigate(`/education/${id}/${formatToSegment(format)}`)
			}
			onClose={async () => {
				// Flush the latest position and wait for the PUT to commit before
				// returning to the Hub, so its GET /progress reads the fresh value.
				await saveProgress();
				navigate("/education");
			}}
			closeLabel={t("article.close", "Close")}
		>
			<div className={styles.content}>
				{!detail && (
					<p className={styles.loading}>{t("article.loading", "Loading…")}</p>
				)}

				{detail && !detail.videoUrl && (
					<div className={styles.pending}>
						<p>
							{t(
								"video.pending",
								"The video for this article is still being prepared.",
							)}
						</p>
					</div>
				)}

				{detail?.videoUrl && (
					<div className={styles.playerCard}>
						<div className={styles.stage}>
							{videoSrc && (
								// biome-ignore lint/a11y/useMediaCaption: the generated video has no caption track artefact yet (same gap as the podcast); the full transcript is the article view
								<video
									ref={videoRef}
									className={styles.video}
									src={videoSrc}
									controls
									playsInline
									preload="metadata"
									poster={posterSrc}
									aria-label={detail.title}
									onLoadedMetadata={(e) => {
										const el = e.currentTarget;
										progressRef.current.duration = el.duration;
										setVideoError(false);
										// Resume where the user left off (Resume carousel), clamped
										// to the media length so a stale/over-long position can't
										// be restored or POSTed.
										if (resumeAt && resumeAt > 0) {
											const at = Number.isFinite(el.duration)
												? Math.min(resumeAt, el.duration)
												: resumeAt;
											el.currentTime = at;
											progressRef.current.current = at;
										}
									}}
									onTimeUpdate={(e) => {
										progressRef.current.current = e.currentTarget.currentTime;
										const now = Date.now();
										if (
											now - lastSaveRef.current >=
											PROGRESS_SAVE_INTERVAL_MS
										) {
											lastSaveRef.current = now;
											saveProgress();
										}
									}}
									onPause={() => saveProgress()}
									onEnded={() => {
										saveProgress();
										logEducation("video_ended", { contentId: id });
									}}
									onPlay={() =>
										logEducation("video_play_toggled", {
											contentId: id,
											playing: true,
										})
									}
									onError={() => setVideoError(true)}
								/>
							)}
						</div>

						{videoError && (
							<p className={styles.videoError}>
								{t("video.error", "The video could not be loaded.")}
							</p>
						)}
					</div>
				)}

				{detail && (
					<section className={styles.aboutCard}>
						<h1 className={styles.title}>{detail.title}</h1>
						{detail.publishedAt && (
							<p className={styles.date}>
								{new Date(detail.publishedAt).toLocaleDateString(locale, {
									year: "numeric",
									month: "long",
									day: "numeric",
								})}
							</p>
						)}
						{meta.description && (
							<p className={styles.aboutBody}>{meta.description}</p>
						)}

						{meta.bullets.length > 0 && (
							<>
								<h2 className={styles.sectionHeading}>
									{t("video.heading", "In this video")}
								</h2>
								<ul className={styles.chapterList}>
									{meta.bullets.map((b) => (
										<li key={b}>{b}</li>
									))}
								</ul>
							</>
						)}

						{meta.disclaimer && (
							<p className={styles.disclaimer}>{meta.disclaimer}</p>
						)}
					</section>
				)}
			</div>
		</EdHubDetailShell>
	);
}
